import { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import './PageStyle.css';
import ApiSender from '../common/ApiLogic.js';
import { v4 as uuidv4 } from 'uuid';

// Mirrors the gateway's own phone.number.national / phone.number.length config (application-local.yml):
// national msisdns are 9 digits starting with one of these prefixes; anything else is rejected server-side too.
const NATIONAL_PREFIXES = ['50', '52', '54', '55', '56', '58'];
// Mirrors AggregatorServiceImpl.getOperator() on the gateway: of the national prefixes above, these three
// resolve to DU, the rest to Etisalat.
const DU_PREFIXES = ['52', '55', '58'];
// The gateway's anti-fraud msisdn fallback (AggregatorServiceImpl.isDuSubscriber) only accepts a fully
// valid msisdn, but we want to react to a DU-looking number well before it's complete - so prefix
// detection here works off whatever's been typed so far rather than requiring full validity.
const ANTI_FRAUD_TIMEOUT_MS = 6000;

// Strips a single leading 0, mirroring PhoneValidation.reformatMsisdn on the gateway, so the operator
// prefix can be read off the start of either accepted shape ("52..." or "052...").
const stripLeadingZero = (m) => (m.startsWith('0') ? m.slice(1) : m);
const isDuPrefix = (m) => DU_PREFIXES.some((prefix) => stripLeadingZero(m).startsWith(prefix));

// Same destination as the "Access App" button on the success page - an already-subscribed
// number has nothing left to do in this funnel, so send it straight there.
const APP_CONTENT_URL = 'https://link-to.app/iRdfLxFVEq';
const ALREADY_SUBSCRIBED_PATTERN = /already\s*(exists?|subscribed)/i;

function PinPage() {
  const apiSender = new ApiSender();
  const nav = useNavigate();
  const location = useLocation();
  const otpContainerRef = useRef();
  const [language, setLanguage] = useState('AR');
  const [correlatorId, setCorrelatorId] = useState(null);
  // True while a DU-looking number is being checked against the real anti-fraud service - the Continue
  // button is disabled for the duration so the user can't opt in before we know whether a real
  // correlatorId is coming. Never set for a non-DU-looking number.
  const [antiFraudPending, setAntiFraudPending] = useState(false);
  const [msisdn, setMsisdn] = useState('');
  const [msisdnEnglish, setMsisdnEnglish] = useState('');
  const [otpEnglish, setOtpEnglish] = useState('');
  const [warning, setWarning] = useState(false);
  const [otp, setOtp] = useState('');
  const [firstStep, setFirstStep] = useState(true);
  const [secondStep, setSecondStep] = useState(false);
  const [message, setMessage] = useState(null);
  const [arMessage, setArMessage] = useState(null);
  const [enMessage, setEnMessage] = useState(null);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const paramsObject = useMemo(() => {
    const obj = {};
    for (const [key, value] of params.entries()) {
      obj[key] = value;
    }
    return obj;
  }, [params]);
  const landingPageUrl = window.location.origin + location.pathname;
  const landingPageName = 'CoreFlex PIN Main LP';
  const [percentage, setPercentage] = useState(0);
  // Flips true the moment optIn is sent, "locking in" whichever correlatorId it used (BE-issued or
  // FE-generated). The anti-fraud call fired from handleMsisdnChange is fire-and-forget and can resolve
  // after the user already hit Continue - without this, that late response would overwrite state with a
  // different id than the one already sent to optIn, so optInConfirm would end up with a third, mismatched
  // value instead of reusing what optIn actually sent.
  const correlatorLockedRef = useRef(false);

  useEffect(() => {
    async function fetchScript() {
      try {
        const result = await apiSender.loadExternalScript(msisdn);
        if (!correlatorLockedRef.current && result.correlatorId) {
          setCorrelatorId(result.correlatorId);
        }
      } catch (error) {
        console.error('Error loading external script:', error);
      }
    }
    fetchScript();
  }, []);

  useEffect(() => {
    apiSender.saveHistory(landingPageUrl, paramsObject);
  }, []);

  useEffect(() => {
    const rawLang = paramsObject?.lang?.toUpperCase();
    const upperLang = rawLang === 'EN' || rawLang === 'AR' ? rawLang : language;
    setLanguage(upperLang);

    const menuToggle = document.getElementById('menuToggle');
    const sidebar = document.getElementById('sidebar');
    const closeSidebar = document.getElementById('closeSidebar');

    const interval = setInterval(() => {
      setPercentage((prev) => {
        if (prev < 100) {
          return prev + 1;
        }
        clearInterval(interval);
        return 100;
      });
    }, 15);

    if (menuToggle && sidebar && closeSidebar) {
      const openSidebar = () => sidebar.classList.add('open');
      const closeSide = () => sidebar.classList.remove('open');

      menuToggle.addEventListener('click', openSidebar);
      closeSidebar.addEventListener('click', closeSide);

      return () => {
        clearInterval(interval);
        menuToggle.removeEventListener('click', openSidebar);
        closeSidebar.removeEventListener('click', closeSide);
      };
    }

    return () => clearInterval(interval);
  }, []);

  const handleLanguage = (value) => {
    setLanguage(value);
  };

  useEffect(() => {
    setMessage(language === 'EN' ? enMessage : arMessage);
  }, [language]);

  useEffect(() => {
    if (secondStep && otpContainerRef?.current) {
      otpContainerRef.current.focus();
    }
  }, [secondStep]);

  const convertDigits = (input) => input
    .replace(/[٠-٩]/g, d => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48))
    .replace(/[^\d]/g, '');

  // Accepts the same two shapes the gateway accepts: a bare 9-digit national number, or the
  // same number with a leading 0 (10 digits) - see PhoneValidation.isMsisdnValid/reformatMsisdn.
  const isMsisdnValid = (m) =>
    typeof m === 'string' &&
    ((m.length === 9 && NATIONAL_PREFIXES.some(p => m.startsWith(p))) ||
      (m.length === 10 && NATIONAL_PREFIXES.some(p => m.startsWith('0' + p))));

  const isOtpValid = (o) => typeof o === 'string' && o.length >= 4;

  const formatMsisdn = (value) => {
    let digits = value.replace(/[^\d٠-٩]/g, '');

    if (!digits.startsWith('5') && !digits.startsWith('0') && !digits.startsWith('٠') && !digits.startsWith('٥')) return '';

    if ((digits.startsWith('0') && digits.charAt(1) && digits.charAt(1) !== '5')
      || (digits.startsWith('٠') && digits.charAt(1) && digits.charAt(1) !== '٥')) {
      digits = digits.charAt(0) + digits.slice(2);
    }

    let part1 = digits.slice(0, 1);
    let part2 = digits.slice(1, 5);
    let part3 = digits.slice(5, 9);
    if (digits.startsWith('05') || digits.startsWith('٠٥')) {
      part1 = digits.slice(0, 2);
      part2 = digits.slice(2, 6);
      part3 = digits.slice(6, 10);
    }

    let formatted = part1;
    if (part2) formatted += ' ' + part2;
    if (part3) formatted += ' ' + part3;

    return formatted;
  };

  const formatOtp = (value) => {
    const digits = value.replace(/[^\d٠-٩]/g, '').slice(0, 6);
    return digits.split('').join(' ');
  };

  // True once the current correlatorId/injected script was obtained *because of* a specific typed
  // msisdn (the dispatch branch below), as opposed to the page-load IP-only check. Only ever set when
  // that page-load check didn't already succeed first - antiFraudScriptLoaded is a shared one-shot flag,
  // so if the IP check had already injected a script, loadExternalScript here would short-circuit
  // immediately with no correlatorId and this would stay false. That's what makes it safe to tear down
  // below without ever touching an IP-sourced script/id.
  const obtainedForTypedMsisdnRef = useRef(false);

  // Second chance to get the anti-fraud script injected for a subscriber the page-load, IP-only check missed
  // (e.g. on WiFi instead of DU's mobile data) but who typed a DU-looking number. Runs on every msisdn
  // change rather than from the input handler directly so it can react to edits (including the user
  // backspacing away from a DU prefix) and clean up a stale in-flight check via the effect's own cleanup.
  //
  // Locks the Continue button (antiFraudPending) the moment the first 3 digits look like a DU prefix -
  // well before the number is complete - so the user can't click through before we know whether a real
  // correlatorId is coming. The actual network call is only dispatched once the number is fully valid,
  // since the gateway's msisdn-based fallback (AggregatorServiceImpl.isDuSubscriber) rejects anything
  // shorter; dispatching early would just waste a round trip. Never locks for a non-DU-looking number.
  //
  // If the user deletes/edits the number away from a DU-looking prefix after we already obtained a
  // correlatorId *for that specific number*, the id and its injected script are torn down too (not just
  // the button unlocked) - they were only ever valid for the number that's now been abandoned, and
  // leaving antiFraudScriptLoaded stuck true would silently block a real check for whatever the user
  // types next.
  useEffect(() => {
    const looksDu = msisdnEnglish.length >= 3 && isDuPrefix(msisdnEnglish);

    if (!looksDu) {
      setAntiFraudPending(false);
      if (obtainedForTypedMsisdnRef.current && !correlatorLockedRef.current) {
        setCorrelatorId(null);
        apiSender.resetAntiFraudScript();
        obtainedForTypedMsisdnRef.current = false;
      }
      return;
    }

    if (correlatorId || correlatorLockedRef.current) {
      setAntiFraudPending(false);
      return;
    }

    setAntiFraudPending(true);

    if (!isMsisdnValid(msisdnEnglish)) {
      return; // still typing - stay locked, don't dispatch yet
    }

    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (!cancelled) setAntiFraudPending(false);
    }, ANTI_FRAUD_TIMEOUT_MS);

    apiSender.loadExternalScript(msisdnEnglish)
      .then((result) => {
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (!correlatorLockedRef.current && result?.correlatorId) {
          obtainedForTypedMsisdnRef.current = true;
          setCorrelatorId(result.correlatorId);
        }
        setAntiFraudPending(false);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        console.error('Error loading external script for msisdn:', error);
        if (!cancelled) setAntiFraudPending(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msisdnEnglish, correlatorId]);

  const handleMsisdnChange = (event) => {
    const newMsisdn = event.target.value;
    setMsisdn(formatMsisdn(newMsisdn));
    const convertMsisdn = convertDigits(newMsisdn);
    setMsisdnEnglish(convertMsisdn);
    setWarning(false);
    setMessage('');
    setArMessage('');
    setEnMessage('');
    if (!isMsisdnValid(convertMsisdn)) {
      document.getElementById('checkmark-container-msisdn').style.opacity = 0;
      document.getElementById('send-otp-btn').classList.remove('active');
      return;
    }
    document.getElementById('checkmark-container-msisdn').style.opacity = 1;
    document.getElementById('send-otp-btn').classList.add('active');
  };

  const handleOtpChange = (event) => {
    const newOtp = event.target.value;
    setOtp(formatOtp(newOtp));
    const convertOtp = convertDigits(newOtp);
    setOtpEnglish(convertOtp);
    setWarning(false);
    setMessage('');
    setArMessage('');
    setEnMessage('');
    if (!isOtpValid(convertOtp)) {
      document.getElementById('checkmark-container-otp').style.opacity = 0;
      document.getElementById('verify-otp-btn').classList.remove('active');
      return;
    }
    document.getElementById('checkmark-container-otp').style.opacity = 1;
    document.getElementById('verify-otp-btn').classList.add('active');
  };

  const handleSendOtpClick = async () => {
    setWarning(false);
    setMessage('');
    setArMessage('');
    setEnMessage('');

    if (!isMsisdnValid(msisdnEnglish)) {
      setMsisdn('');
      setMsisdnEnglish('');
      setMessage(language === 'EN' ? 'Incorrect Phone Number Format' : 'رقم غير صحيح');
      setWarning(true);
      return;
    }

    // Button is disabled while antiFraudPending, so by the time this runs for a DU-looking number the
    // check has already succeeded, failed, or timed out - never left mid-flight. If it never returned a
    // correlatorId (non-DU number, the check failed/timed out), mint one on the FE here instead of
    // sending null. Persisting it into state means handleVerifyOtpClick's optInConfirm call below reuses
    // this exact id too, same as it would reuse a BE-issued one. Locking here also blocks the anti-fraud
    // effect above from overwriting whichever id we commit to below, in case it somehow still fires.
    const effectiveCorrelatorId = correlatorId || uuidv4();
    correlatorLockedRef.current = true;
    if (!correlatorId) {
      setCorrelatorId(effectiveCorrelatorId);
    }

    document.getElementById('loading-spinner-send').style.display = 'inline-flex';
    document.getElementById('send-otp-btn').disabled = true;
    const result = await apiSender.optIn(msisdnEnglish, language, effectiveCorrelatorId, landingPageUrl, landingPageName, paramsObject);
    document.getElementById('loading-spinner-send').style.display = 'none';
    document.getElementById('send-otp-btn').disabled = false;

    if (result.success) {
      if (result.redirectUrl) {
        window.location.replace(result.redirectUrl);
        return;
      }
      setFirstStep(false);
      setSecondStep(true);
      return;
    }
    if (ALREADY_SUBSCRIBED_PATTERN.test(result.message || '')) {
      window.location.href = APP_CONTENT_URL;
      return;
    }
    setArMessage(result.arMessage);
    setEnMessage(result.message);
    setMessage(language === 'EN' ? result.message : result.arMessage);
    setWarning(true);
  };

  const handleVerifyOtpClick = async () => {
    setWarning(false);
    setMessage('');
    setArMessage('');
    setEnMessage('');

    if (!isOtpValid(otpEnglish)) {
      setOtp('');
      setOtpEnglish('');
      setMessage(language === 'EN' ? 'Incorrect PIN Code' : 'رمز سري غير صحيح');
      setWarning(true);
      return;
    }

    const otpId = uuidv4() + '-' + msisdnEnglish + '-' + new Date().getTime();
    document.getElementById('loading-spinner-verify').style.display = 'inline-flex';
    document.getElementById('verify-otp-btn').disabled = true;
    const result = await apiSender.optInConfirm(msisdnEnglish, language, otpEnglish, otpId, correlatorId, landingPageUrl, landingPageName, paramsObject);
    document.getElementById('loading-spinner-verify').style.display = 'none';
    document.getElementById('verify-otp-btn').disabled = false;
    if (result.success) {
      nav(`/success?lang=${language}`);
      return;
    }
    if (ALREADY_SUBSCRIBED_PATTERN.test(result.message || '')) {
      window.location.href = APP_CONTENT_URL;
      return;
    }
    setWarning(true);
    setArMessage(result.arMessage);
    setEnMessage(result.message);
    setMessage(language === 'EN' ? result.message : result.arMessage);
    setOtp('');
    setOtpEnglish('');
  };

  return (
    <div className="App">
      <div className='top'>
        <div className='title' dir={language === 'EN' ? 'ltr' : 'rtl'}>{language === 'EN' ? 'Free for 24 hours then AED 2.63/day (Etisalat) (VAT Inclusive)' : 'مجاناً لمدة 24 ساعة، ثم AED 2.63/يوم (اتصالات) (شامل ضريبة القيمة المضافة)'}</div>
        <div className='bar'>
          <div className="bar-left">
            <img className="logo" src="/logo.png" alt="CoreFlex"></img>
          </div>
          <div className="bar-right">
            <div className='language'>
              <button className={language === 'EN' ? 'active' : ''} onClick={() => handleLanguage('EN')}>EN</button>
              <button className={language === 'AR' ? 'active' : ''} onClick={() => handleLanguage('AR')}>AR</button>
            </div>
            <div className="menu-label" id="menuToggle">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
      <div className="sidebar" id="sidebar">
        <div className="close-btn" id="closeSidebar">&times;</div>
        <ul className="menu-items">
          <li dir={language === 'EN' ? 'ltr' : 'rtl'}><Link to={`/terms?lang=${language}`}>{language === 'EN' ? 'Terms & Conditions' : 'الأحكام والشروط'}</Link></li>
          <li dir={language === 'EN' ? 'ltr' : 'rtl'}><Link to={`/privacy?lang=${language}`}>{language === 'EN' ? 'Privacy Policy' : 'سياسة الخصوصية'}</Link></li>
          <li dir={language === 'EN' ? 'ltr' : 'rtl'} onClick={() => window.location.href = 'mailto:support@coreflex.fun'}>{language === 'EN' ? 'Contact us' : 'معلومات الاتصال'}</li>
        </ul>
      </div>
      <div className='middle'>
        <div className='title' dir={language === 'EN' ? 'ltr' : 'rtl'}>{language === 'EN' ? 'Start working out on your mobile with all types of fitness workouts' : 'ابدأ في ممارسة التمارين الرياضية على هاتفك المحمول بجميع أنواعها'}</div>
        <div className="banner">
          {percentage !== 100 ? (
            <div className="circle-wrapper">
              <svg className="progress-ring" viewBox="0 0 36 36">
                <path className="progress-ring__bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <path className="progress-ring__value" strokeDasharray="100, 100" strokeDashoffset={100 - percentage} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              </svg>
              <div className="percentage-text">
                {percentage}%
              </div>
            </div>)
            : (<img className="logo" src="/banner_activate.png" alt="CoreFlex"></img>
            )}
        </div>
        <div className='input-content'>
          <div className='title' dir={language === 'EN' ? 'ltr' : 'rtl'}><p>{firstStep ? (language === 'EN' ? 'Enter your mobile number to start' : 'أدخل رقم هاتفك المحمول للبدء') : (language === 'EN' ? 'Enter OTP' : 'أدخل كلمة المرور')}</p></div>
          {firstStep &&
            <div id="msisdn-input" className='phoneInputWrap'>
              <div className="phone-input">
                <div className="phone"></div>
                <input type="tel" value={msisdn} autoComplete="off" id="phone-input" name="phone-input-name" placeholder={language === 'EN' ? '(0)5 XXXX XXXX' : '(٠)٥ xxxx xxxx'} minLength={9} maxLength={msisdn.startsWith('5') || msisdn.startsWith('٥') ? 11 : 12} className="text-input" autoFocus onChange={handleMsisdnChange} />
                <div id="checkmark-container-msisdn" className="checkmark-container" data-checkmark-container="true" style={{ opacity: 0 }}>
                  <svg id="success-check-mark-msisdn" className="checkmark__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                    <circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none"></circle>
                    <path className="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"></path>
                  </svg>
                </div>
              </div>
            </div>
          }

          {secondStep &&
            <div id="otp-input" className='phoneInputWrap'>
              <div className="phone-input">
                <div className="lock"></div>
                <input type="tel" value={otp} autoComplete="off" id="phone-input" name="otp-input-name" ref={otpContainerRef} placeholder="X X X X" minLength={4} maxLength={11} className="text-input" autoFocus onChange={handleOtpChange} />
                <div id="checkmark-container-otp" className="checkmark-container" data-checkmark-container="true" style={{ opacity: 0 }}>
                  <svg id="success-check-mark-otp" className="checkmark__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                    <circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none"></circle>
                    <path className="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"></path>
                  </svg>
                </div>
              </div>
            </div>
          }

          <div className='button'>
            {
              firstStep &&
              <button id='send-otp-btn' disabled={antiFraudPending} onClick={handleSendOtpClick}>
                {language === 'EN' ? 'CONTINUE' : 'تابع'}<span>{language === 'EN' ? 'to subscribe' : 'للاشتراك'}</span>
                <div id="loading-spinner-send" className="spinner" style={antiFraudPending ? { display: 'inline-flex' } : undefined}></div>
              </button>
            }
            {
              secondStep &&
              <button id='verify-otp-btn' onClick={handleVerifyOtpClick}>
                {language === 'EN' ? 'SUBSCRIBE' : 'اشتراك'}
                <div id="loading-spinner-verify" className="spinner"></div>
              </button>
            }
          </div>

          <div id='warning-msg-div' className={`warning-msg ${warning ? 'active' : ''}`} dir={language === 'EN' ? 'ltr' : 'rtl'}>
            <span id='warning-msg'>{message}</span>
          </div>

          <div className='exit-content' dir={language === 'EN' ? 'ltr' : 'rtl'}>
            <a href="https://www.google.ae/">{language === 'EN' ? 'Exit' : 'خروج'}</a>
            <p>{language === 'EN' ? 'Free for 24 hours then AED 2.63/day (Etisalat) (VAT Inclusive)' : 'مجاناً لمدة 24 ساعة، ثم AED 2.63/يوم (اتصالات) (شامل ضريبة القيمة المضافة)'}</p>
            <p>{language === 'EN' ? 'After clicking \'Subscribe\' you will receive a PIN message to confirm your subscription.' : 'بالضغط على \'اشترك\'، سوف تتلقى رسالة تحتوي علي رمز PIN لتأكيد اشتراكك.'}</p>
          </div>

        </div>
      </div>
      <div className='bottom'>
        <div className='disclaimer' dir={language === 'EN' ? 'ltr' : 'rtl'} style={{ textAlign: language === 'EN' ? 'left' : 'right' }}>
          {language === 'EN' ? 'TERMS AND CONDITIONS:' : 'الشروط والأحكام:'}
          <br></br>
          {language === 'EN' ? 'By Clicking on Subscribe, you agree to the below terms and conditions:' : 'بالضغط على زر اشترك أعلاه، فأنت توافق على الشروط والأحكام التالية:'}
          <ul className='disclaimer-list'>
            <li>{language === 'EN' ? 'You will start the paid subscription after the free period automatically' : 'سوف يبدأ الاشتراك المدفوع تلقائياً بعد فترة التجربة المجانية'}</li>
            <li>{language === 'EN' ? <>No commitment, you can cancel your subscription at any time by using <Link to={`/unsub?lang=${language}`}>this link</Link> or by sending "Deact C" to 4273 for Etisalat and 2837 for DU</> : <>بدون أي التزامات، يمكنك إلغاء اشتراكك في أي وقت عن طريق <Link to={`/unsub?lang=${language}`}>هذا الرابط</Link> أو عن طريق إرسال "Deact C" إلى 4273 لمشتركي اتصالات و2837 لمشتركي دو</>}</li>
            <li>{language === 'EN' ? 'To get support, please contact' : 'للحصول على الدعم ، يرجى الاتصال علي'} <a href='mailto:support@coreflex.fun'>support@coreflex.fun</a></li>
            <li>{language === 'EN' ? 'The free trial is valid only for new subscribers' : 'التجربة المجانية صالحة فقط للمشتركين لأول مرة'}</li>
            <li>{language === 'EN' ? 'Enjoy your Free trial for 24 hours (Etisalat only)' : 'استمتع بفترة التجربة المجانية لمدة 24 ساعة (اتصالات فقط)'}</li>
            <li>{language === 'EN' ? 'Please make sure that your browser is not using any 3rd-party blocking technologies and you have a healthy internet connection for swift access to the content.' : 'يرجى التأكد من عدم استخدام أي تقنيات حجب من الطرف الثالث على متصفحك والتأكد من وجود اتصال إنترنت جيد لضمان الوصول السريع للمحتوى.'}</li>
            <li>{language === 'EN' ? 'Further Terms and conditions' : 'للشروط والأحكام الإضافية برجاء'} <Link to={`/terms?lang=${language}`}>{language === 'EN' ? 'click here' : 'الضغط هنا'}</Link> {language === 'EN' ? '& Privacy Policy' : 'وسياسة الخصوصية برجاء'} <Link to={`/privacy?lang=${language}`}>{language === 'EN' ? 'click here' : 'الضغط هنا'}</Link></li>
            <li>{language === 'EN' ? 'By proceeding, you are accepting all Terms and Conditions of the service and agree to receive updates about your subscription on your registered mobile number.' : 'بالمتابعة، أنت توافق على جميع الشروط والأحكام الخاصة بالخدمة وتوافق على استلام تحديثات حول اشتراكك على رقم الجوال المسجل'}</li>
          </ul>
        </div>
        <ul className='links'>
          <li dir={language === 'EN' ? 'ltr' : 'rtl'}><Link to={`/unsub?lang=${language}`}>{language === 'EN' ? 'Unsubscribe' : 'الغاء الاشتراك'}</Link></li>
          <li dir={language === 'EN' ? 'ltr' : 'rtl'}><Link to={`/terms?lang=${language}`}>{language === 'EN' ? 'Terms & Conditions' : 'الأحكام والشروط'}</Link></li>
          <li dir={language === 'EN' ? 'ltr' : 'rtl'}><Link to={`/privacy?lang=${language}`}>{language === 'EN' ? 'Privacy Policy' : 'سياسة الخصوصية'}</Link></li>
          <li dir={language === 'EN' ? 'ltr' : 'rtl'}><a href='mailto:support@coreflex.fun'>{language === 'EN' ? 'Contact Us' : 'معلومات الاتصال'}</a></li>
        </ul>
      </div>
    </div>
  );
}

export default PinPage;
